import { useState, useRef, useEffect } from "react";
import axios, { getApiUrl } from "@/lib/api";
import { Download, Upload, FileJson, AlertCircle, CheckCircle2, FileSpreadsheet, Loader2, Globe, Copy, Link } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { AdBanner } from "@/components/AdSense";
import ProGate from "@/components/ProGate";

const API = `${getApiUrl()}/api`;

export default function ImportExport() {
  const navigate = useNavigate();
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importMode, setImportMode] = useState("merge");
  const [importStats, setImportStats] = useState(null);
  const [showReplaceWarning, setShowReplaceWarning] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  
  // Spreadsheet import state
  const [spreadsheetImporting, setSpreadsheetImporting] = useState(false);
  const [spreadsheetStats, setSpreadsheetStats] = useState(null);

  // TrueGaming CSV import state
  const [trueImporting, setTrueImporting] = useState(false);
  const [trueImportResult, setTrueImportResult] = useState(null);
  const trueFileInputRef = useRef(null);
  
  // Shared game import state
  const [sharedImporting, setSharedImporting] = useState(false);
  const [sharedImportResult, setSharedImportResult] = useState(null);
  
  // True* HTML import state (unified)
  const [htmlDialogOpen, setHtmlDialogOpen] = useState(false);
  const [htmlInput, setHtmlInput] = useState("");
  const [htmlGameName, setHtmlGameName] = useState("");
  const [htmlSystem, setHtmlSystem] = useState("");
  const [htmlImporting, setHtmlImporting] = useState(false);
  const [htmlImportResult, setHtmlImportResult] = useState(null);
  
  const [xboxConnected, setXboxConnected] = useState(false);
  
  useEffect(() => {
    // Check Xbox connection status
    checkXboxStatus();
  }, []);
  
  const checkXboxStatus = async () => {
    try {
      const response = await axios.get(`${API}/xbox/status`);
      setXboxConnected(response.data.configured && response.data.gamertag);
    } catch (error) {
      console.error("Failed to check Xbox status:", error);
    }
  };

  // Unified HTML Import handler (supports all platforms)
  const handleHtmlImport = async () => {
    if (!htmlInput.trim()) {
      toast.error("Please paste the page source HTML");
      return;
    }
    await executeHtmlImport();
  };
  
  const executeHtmlImport = async () => {
    setHtmlImporting(true);
    setHtmlImportResult(null);
    
    try {
      const response = await axios.post(`${API}/unified/import-html`, {
        html: htmlInput,
        gameName: htmlGameName || undefined,
        system: htmlSystem || undefined
      });
      
      setHtmlImportResult(response.data);
      toast.success(response.data.message);
      
      // Clear form on success
      setHtmlInput("");
      setHtmlGameName("");
      setHtmlSystem("");
    } catch (error) {
      console.error("True* HTML import error:", error);
      const errorMsg = error.response?.data?.detail || "Failed to import. Make sure you copied the full page source.";
      setHtmlImportResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setHtmlImporting(false);
    }
  };

  // TrueGaming CSV Import handler
  const handleTrueImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error("Please select a CSV file");
      return;
    }

    await executeTrueImport(file);
  };
  
  const executeTrueImport = async (file) => {
    setTrueImporting(true);
    setTrueImportResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/import/true-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setTrueImportResult(response.data);
      
      if (response.data.success) {
        toast.success(
          `Imported ${response.data.achievementsImported} achievements from ${response.data.gamesImported} games!`
        );
      }
    } catch (error) {
      console.error("CSV import error:", error);
      toast.error(error.response?.data?.detail || "Failed to import CSV");
    } finally {
      setTrueImporting(false);
      // Reset file input
      if (trueFileInputRef.current) {
        trueFileInputRef.current.value = '';
      }
    }
  };

  // Shared Game Import handler
  const handleSharedGameImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast.error("Please select a JSON file");
      return;
    }

    setSharedImporting(true);
    setSharedImportResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/games/import-shared`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSharedImportResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error("Shared game import error:", error);
      toast.error(error.response?.data?.detail || "Failed to import shared game");
    } finally {
      setSharedImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/export`);
      const data = response.data;
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `gamepop-export-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Data exported successfully!");
    } catch (error) {
      console.error("Failed to export:", error);
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (importMode === "replace") {
      setPendingFile(file);
      setShowReplaceWarning(true);
    } else {
      processImport(file);
    }
  };

  const processImport = async (file) => {
    setImporting(true);
    setImportStats(null);

    try {
      const text = await file.text();
      let jsonData;
      
      try {
        jsonData = JSON.parse(text);
      } catch (e) {
        toast.error("Invalid JSON file");
        return;
      }

      if (!jsonData.data) {
        toast.error("Invalid export file format. Missing 'data' field.");
        return;
      }

      const response = await axios.post(`${API}/import`, {
        data: jsonData.data,
        mode: importMode,
      });

      setImportStats(response.data.stats);
      toast.success("Import completed!");
    } catch (error) {
      console.error("Failed to import:", error);
      toast.error(error.response?.data?.detail || "Failed to import data");
    } finally {
      setImporting(false);
      setPendingFile(null);
    }
  };

  const confirmReplace = () => {
    if (pendingFile) {
      processImport(pendingFile);
    }
    setShowReplaceWarning(false);
  };

  const handleSpreadsheetImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    await executeSpreadsheetImport(file, event);
  };
  
  const executeSpreadsheetImport = async (file, event) => {
    setSpreadsheetImporting(true);
    setSpreadsheetStats(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/import-spreadsheet`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSpreadsheetStats(response.data.stats);
      toast.success("Spreadsheet import completed!");
    } catch (error) {
      console.error("Failed to import spreadsheet:", error);
      toast.error(error.response?.data?.detail || "Failed to import spreadsheet");
    } finally {
      setSpreadsheetImporting(false);
      // Reset file input
      if (event?.target) event.target.value = "";
    }
  };

  return (
    <ProGate feature="export">
    <div data-testid="import-export-page" className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-white tracking-tight">
          Import / Export
        </h1>
        <p className="text-white/50 mt-1">Backup your data or import from spreadsheets and exports</p>
      </div>

      {/* Shared Game Import/Export Card - Featured at top */}
      <Card className="bg-[#0A0A0A] border-purple-500/30">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <Globe className="w-5 h-5 text-purple-400" />
            Share Game Configurations
          </CardTitle>
          <CardDescription className="text-white/50">
            Import game setups shared by other users, or export your own to share. Includes game info, DLCs, and achievements (without personal unlock data).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-[#121212] rounded-lg border border-white/5">
            <h4 className="text-white font-medium mb-2">What&apos;s shared:</h4>
            <ul className="text-white/60 text-sm space-y-1">
              <li>• Game metadata (name, platform, box art, etc.)</li>
              <li>• DLC structure and organization</li>
              <li>• All achievements with descriptions and scores</li>
              <li>• Achievement-to-DLC assignments</li>
            </ul>
            <p className="text-white/40 text-xs mt-3">
              Your existing unlock data will be preserved when importing.
            </p>
          </div>

          <div className="border-2 border-dashed border-white/20 rounded-lg p-6 text-center hover:border-purple-400/50 transition-colors">
            <input
              type="file"
              accept=".json"
              onChange={handleSharedGameImport}
              className="hidden"
              id="shared-game-file-input"
              data-testid="shared-game-file-input"
              disabled={sharedImporting}
            />
            <label
              htmlFor="shared-game-file-input"
              className="cursor-pointer flex flex-col items-center gap-3"
            >
              {sharedImporting ? (
                <>
                  <Loader2 className="w-10 h-10 text-purple-400 animate-spin" />
                  <span className="text-white/60">Importing shared game...</span>
                </>
              ) : (
                <>
                  <Globe className="w-10 h-10 text-purple-400/60" />
                  <div>
                    <span className="text-white font-medium">Click to import a shared game file</span>
                    <p className="text-white/40 text-xs mt-1">
                      Select a .json file exported from another user&apos;s Game-o-Rang
                    </p>
                  </div>
                </>
              )}
            </label>
          </div>

          <p className="text-white/40 text-xs text-center">
            To export a game, go to the game&apos;s detail page → click ⋮ menu → &quot;Export &amp; Share&quot;
          </p>

          {sharedImportResult && (
            <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-5 h-5 text-purple-400" />
                <h4 className="text-purple-400 font-medium">
                  {sharedImportResult.gameCreated ? "Game Imported!" : "Game Updated!"}
                </h4>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-white/60">Game:</div>
                <div className="text-white">{sharedImportResult.gameName}</div>
                <div className="text-white/60">DLCs Created:</div>
                <div className="text-white">{sharedImportResult.dlcsCreated}</div>
                <div className="text-white/60">Achievements Added:</div>
                <div className="text-white">{sharedImportResult.achievementsAdded}</div>
                <div className="text-white/60">Achievements Updated:</div>
                <div className="text-white">{sharedImportResult.achievementsUpdated}</div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="mt-3 border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                onClick={() => navigate(`/games/${sharedImportResult.gameId}`)}
              >
                View Game
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* TrueGaming Sites Import */}
      <Card className="bg-[#0A0A0A] border-purple-500/30">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-purple-400" />
            Import from TrueGaming Sites
          </CardTitle>
          <CardDescription className="text-white/50">
            Import achievements from TrueAchievements, TrueTrophies, or TrueSteamAchievements CSV exports.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="p-3 bg-[#107C10]/10 rounded-lg border border-[#107C10]/30">
              <p className="text-[#107C10] font-bold text-sm">TrueAchievements</p>
              <p className="text-white/40 text-xs">Xbox achievements</p>
            </div>
            <div className="p-3 bg-[#003087]/10 rounded-lg border border-[#003087]/30">
              <p className="text-[#003087] font-bold text-sm">TrueTrophies</p>
              <p className="text-white/40 text-xs">PlayStation trophies</p>
            </div>
            <div className="p-3 bg-[#66C0F4]/10 rounded-lg border border-[#66C0F4]/30">
              <p className="text-[#66C0F4] font-bold text-sm">TrueSteamAchievements</p>
              <p className="text-white/40 text-xs">Steam achievements</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <input
              ref={trueFileInputRef}
              type="file"
              accept=".csv"
              onChange={handleTrueImport}
              className="hidden"
              id="true-csv-upload"
              data-testid="true-csv-upload"
            />
            <Button
              onClick={() => trueFileInputRef.current?.click()}
              disabled={trueImporting}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="import-true-csv-btn"
            >
              {trueImporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload CSV
                </>
              )}
            </Button>
          </div>

          {/* Import Result */}
          {trueImportResult && (
            <div className={`p-4 rounded-lg border ${
              trueImportResult.success 
                ? 'bg-green-500/10 border-green-500/30' 
                : 'bg-red-500/10 border-red-500/30'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className={`w-4 h-4 ${trueImportResult.success ? 'text-green-400' : 'text-red-400'}`} />
                <span className="text-white font-medium">
                  Import {trueImportResult.success ? 'Complete' : 'Failed'}
                </span>
              </div>
              {trueImportResult.success && (
                <>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-white/50">Source:</span>
                      <span className="text-white ml-2 capitalize">{trueImportResult.source}</span>
                    </div>
                    <div>
                      <span className="text-white/50">Platform:</span>
                      <span className="text-white ml-2 capitalize">{trueImportResult.platform}</span>
                    </div>
                    <div>
                      <span className="text-white/50">Games:</span>
                      <span className="text-green-400 ml-2">+{trueImportResult.gamesImported}</span>
                      {trueImportResult.dlcsCreated > 0 && (
                        <span className="text-purple-400 ml-1">(+{trueImportResult.dlcsCreated} DLCs)</span>
                      )}
                      {trueImportResult.gamesUpdated > 0 && (
                        <span className="text-yellow-400 ml-1">({trueImportResult.gamesUpdated} updated)</span>
                      )}
                    </div>
                    <div>
                      <span className="text-white/50">Achievements:</span>
                      <span className="text-green-400 ml-2">+{trueImportResult.achievementsImported}</span>
                      {trueImportResult.achievementsUpdated > 0 && (
                        <span className="text-yellow-400 ml-1">({trueImportResult.achievementsUpdated} updated)</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Xbox Auto-Sync Results for Xbox imports */}
                  {trueImportResult.platform === 'xbox' && (
                    <div className="mt-4 p-3 bg-[#107C10]/10 rounded-lg border border-[#107C10]/30">
                      <div className="flex items-center gap-2 mb-2">
                        <img src="/platform-logos/xbox-green.png" alt="Xbox" className="w-4 h-4 object-contain" />
                        <span className="text-[#107C10] font-medium text-sm">Xbox Locked Achievements</span>
                      </div>
                      {trueImportResult.xboxSyncResults ? (
                        <div className="space-y-2">
                          <p className="text-white/70 text-xs">
                            <CheckCircle2 className="w-3 h-3 inline mr-1 text-green-400" />
                            Auto-synced {trueImportResult.xboxSyncResults.synced?.length || 0} games
                          </p>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-white/50">Locked achievements added:</span>
                              <span className="text-green-400 ml-2">+{trueImportResult.xboxSyncResults.totalAdded || 0}</span>
                            </div>
                            <div>
                              <span className="text-white/50">Updated:</span>
                              <span className="text-yellow-400 ml-2">{trueImportResult.xboxSyncResults.totalUpdated || 0}</span>
                            </div>
                          </div>
                          {trueImportResult.xboxSyncResults.failed?.length > 0 && (
                            <p className="text-red-400/80 text-xs">
                              {trueImportResult.xboxSyncResults.failed.length} games could not be synced (Title ID not found)
                            </p>
                          )}
                        </div>
                      ) : trueImportResult.xboxSyncMessage ? (
                        <div className="space-y-2">
                          <p className="text-white/60 text-xs">{trueImportResult.xboxSyncMessage}</p>
                          {!xboxConnected && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-[#107C10]/50 text-[#107C10] hover:bg-[#107C10]/10"
                              onClick={() => navigate('/settings')}
                            >
                              <Link className="w-3 h-3 mr-2" />
                              Set Up Gamertag
                            </Button>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}
                </>
              )}
              {trueImportResult.errors?.length > 0 && (
                <div className="mt-2 text-xs text-red-400">
                  <p className="font-medium">Errors:</p>
                  <ul className="list-disc list-inside">
                    {trueImportResult.errors.slice(0, 5).map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <p className="text-white/40 text-xs">
            <strong>How to export:</strong> Go to &quot;My Achievements&quot; or &quot;My Trophies&quot; on the TrueGaming site, 
            then click the Export/Download button to get your CSV file. The import will automatically create DLC entries for achievements that belong to expansion packs.
          </p>
          <p className="text-amber-500/90 text-xs mt-2 p-2 bg-amber-500/10 rounded border border-amber-500/20">
            <strong>⚠️ Pro Subscription Required:</strong> Downloading CSV exports from TrueAchievements, TrueTrophies, or TrueSteamAchievements requires a Pro subscription on that site. 
            Free users can use the <strong>&quot;Import from Page Source&quot;</strong> option below to import games one at a time.
          </p>
          <p className="text-yellow-400/80 text-xs mt-2">
            <strong>Warning:</strong> CSV will not import all unlocked achievements or trophies if they were unlocked offline. Please make sure to check for any discrepancies in your scores and achievement/trophy counts.
          </p>
          {xboxConnected && (
            <p className="text-[#107C10]/80 text-xs mt-2">
              <strong>Auto-sync enabled:</strong> When you import Xbox achievements, locked achievements will be automatically fetched from Xbox Live.
            </p>
          )}
        </CardContent>
      </Card>

      {/* True* HTML Import Card */}
      <Card className="bg-[#0A0A0A] border-cyan-500/30">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <Globe className="w-5 h-5 text-cyan-400" />
            Import from Page Source (HTML)
          </CardTitle>
          <CardDescription className="text-white/50">
            Import a single game by copying the page source from TrueAchievements, TrueTrophies, or TrueSteamAchievements.
            The source site is automatically detected.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="p-3 bg-[#107C10]/10 rounded-lg border border-[#107C10]/30">
              <p className="text-[#107C10] font-bold text-sm">TrueAchievements</p>
              <p className="text-white/40 text-xs">Xbox achievements</p>
            </div>
            <div className="p-3 bg-[#003087]/10 rounded-lg border border-[#003087]/30">
              <p className="text-[#003087] font-bold text-sm">TrueTrophies</p>
              <p className="text-white/40 text-xs">PlayStation trophies</p>
            </div>
            <div className="p-3 bg-[#66C0F4]/10 rounded-lg border border-[#66C0F4]/30">
              <p className="text-[#66C0F4] font-bold text-sm">TrueSteamAchievements</p>
              <p className="text-white/40 text-xs">Steam achievements</p>
            </div>
            <div className="p-3 bg-gray-500/10 rounded-lg border border-gray-500/30">
              <p className="text-gray-300 font-bold text-sm">Epic Games Store</p>
              <p className="text-white/40 text-xs">Epic achievements</p>
            </div>
          </div>

          <Dialog open={htmlDialogOpen} onOpenChange={setHtmlDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="import-html-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Import from Page Source
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white">Import from Page Source</DialogTitle>
                <DialogDescription className="text-white/60">
                  Paste the page source from any supported site. The source will be auto-detected.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                  <p className="text-cyan-300 text-sm font-medium mb-2">How to get page source:</p>
                  <ol className="text-white/60 text-xs space-y-1 list-decimal list-inside">
                    <li>Go to a game&apos;s achievement page on any supported site</li>
                    <li>Right-click anywhere on the page</li>
                    <li>Select &quot;View Page Source&quot; or press <kbd className="bg-white/10 px-1 rounded">Ctrl+U</kbd></li>
                    <li>Select all (<kbd className="bg-white/10 px-1 rounded">Ctrl+A</kbd>) and copy (<kbd className="bg-white/10 px-1 rounded">Ctrl+C</kbd>)</li>
                    <li>Paste below</li>
                  </ol>
                  <p className="text-white/50 text-xs mt-2">
                    <strong>Supported:</strong> TrueAchievements, TrueTrophies, TrueSteamAchievements, Epic Games Store
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-white/80">Game Name (optional - auto-detected)</Label>
                  <Input
                    placeholder="Leave blank to auto-detect from page"
                    value={htmlGameName}
                    onChange={(e) => setHtmlGameName(e.target.value)}
                    className="bg-black/50 border-white/10"
                    data-testid="html-game-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/80">System (Xbox only - optional)</Label>
                  <Select value={htmlSystem} onValueChange={setHtmlSystem}>
                    <SelectTrigger className="bg-black/50 border-white/10">
                      <SelectValue placeholder="Auto-detect (or select for Xbox)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto-detect</SelectItem>
                      <SelectItem value="Xbox Series X">Xbox Series X</SelectItem>
                      <SelectItem value="Xbox One">Xbox One</SelectItem>
                      <SelectItem value="Xbox 360">Xbox 360</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-white/80">Page Source HTML *</Label>
                  <Textarea
                    placeholder="Paste the entire page source here..."
                    value={htmlInput}
                    onChange={(e) => setHtmlInput(e.target.value)}
                    className="bg-black/50 border-white/10 h-48 font-mono text-xs"
                    data-testid="html-source-input"
                  />
                </div>

                {htmlImportResult && (
                  <div className={`p-3 rounded-lg border ${
                    htmlImportResult.success 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : 'bg-red-500/10 border-red-500/30'
                  }`}>
                    {htmlImportResult.success ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-400" />
                          <span className="text-green-300 font-medium">Import Successful</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-white/50">Source:</span>
                            <span className="text-white ml-2">{htmlImportResult.detectedSource}</span>
                          </div>
                          <div>
                            <span className="text-white/50">Game:</span>
                            <span className="text-white ml-2">{htmlImportResult.gameName}</span>
                          </div>
                          <div>
                            <span className="text-white/50">System:</span>
                            <span className={`ml-2 ${htmlImportResult.system ? 'text-white' : 'text-yellow-400'}`}>
                              {htmlImportResult.system || 'Not set'}
                            </span>
                          </div>
                          <div>
                            <span className="text-white/50">Achievements:</span>
                            <span className="text-green-400 ml-2">
                              +{htmlImportResult.achievementsAdded}
                              {htmlImportResult.achievementsUpdated > 0 && (
                                <span className="text-yellow-400 ml-1">({htmlImportResult.achievementsUpdated} updated)</span>
                              )}
                            </span>
                          </div>
                          {htmlImportResult.dlcsCreated > 0 && (
                            <div>
                              <span className="text-white/50">DLCs:</span>
                              <span className="text-purple-400 ml-2">+{htmlImportResult.dlcsCreated}</span>
                            </div>
                          )}
                        </div>
                        {/* Show detected systems if multiple found */}
                        {htmlImportResult.detectedSystems?.length > 1 && (
                          <div className="mt-2 p-2 bg-yellow-500/10 rounded border border-yellow-500/30">
                            <p className="text-yellow-300 text-xs">
                              <strong>Multiple systems detected:</strong> {htmlImportResult.detectedSystems.join(', ')}
                            </p>
                            <p className="text-white/50 text-xs mt-1">
                              Please set the correct system in the game settings.
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-red-400" />
                        <span className="text-red-300">{htmlImportResult.error}</span>
                      </div>
                    )}
                  </div>
                )}

                <Button
                  onClick={handleHtmlImport}
                  disabled={htmlImporting || !htmlInput.trim()}
                  className="w-full bg-cyan-600 hover:bg-cyan-700"
                  data-testid="html-import-submit-btn"
                >
                  {htmlImporting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import Game
                    </>
                  )}
                </Button>
                
              </div>
            </DialogContent>
          </Dialog>

          <p className="text-white/40 text-xs">
            <strong>Tip:</strong> Use this method to import a specific game with full achievement details including DLCs.
            For bulk imports, use the CSV method above.
          </p>
        </CardContent>
      </Card>

      {/* Spreadsheet Import Card */}
      <Card className="bg-[#0A0A0A] border-white/5">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-orange-400" />
            Import from Spreadsheet
          </CardTitle>
          <CardDescription className="text-white/50">
            Import multiple games and achievements from a CSV or Excel file.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-[#121212] rounded-lg border border-white/5">
            <h4 className="text-white font-medium mb-3">Required Columns:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-green-400 font-mono text-xs bg-green-400/10 px-1.5 py-0.5 rounded">Game</span>
                <span className="text-white/60">Game name (required)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-white/40 font-mono text-xs bg-white/5 px-1.5 py-0.5 rounded">DLC</span>
                <span className="text-white/60">DLC name (blank for base game)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-400 font-mono text-xs bg-green-400/10 px-1.5 py-0.5 rounded">Name</span>
                <span className="text-white/60">Achievement name (required)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-white/40 font-mono text-xs bg-white/5 px-1.5 py-0.5 rounded">Unlock Date</span>
                <span className="text-white/60">Date unlocked (blank = locked)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-white/40 font-mono text-xs bg-white/5 px-1.5 py-0.5 rounded">Unlock Time</span>
                <span className="text-white/60">Time unlocked (optional)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-400 font-mono text-xs bg-green-400/10 px-1.5 py-0.5 rounded">Platform</span>
                <span className="text-white/60">xbox, playstation, steam, etc.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-400 font-mono text-xs bg-green-400/10 px-1.5 py-0.5 rounded">System</span>
                <span className="text-white/60">Xbox One, PS4, PC, etc.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-white/40 font-mono text-xs bg-white/5 px-1.5 py-0.5 rounded">Score</span>
                <span className="text-white/60">Points value</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-white/40 font-mono text-xs bg-white/5 px-1.5 py-0.5 rounded">D/C</span>
                <span className="text-white/60">Discontinued (yes/no)</span>
              </div>
            </div>
          </div>

          <div className="border-2 border-dashed border-white/20 rounded-lg p-6 text-center hover:border-orange-400/50 transition-colors">
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleSpreadsheetImport}
              className="hidden"
              id="spreadsheet-file-input"
              data-testid="spreadsheet-file-input"
              disabled={spreadsheetImporting}
            />
            <label
              htmlFor="spreadsheet-file-input"
              className="cursor-pointer flex flex-col items-center gap-3"
            >
              {spreadsheetImporting ? (
                <>
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-400"></div>
                  <span className="text-white/60">Importing...</span>
                </>
              ) : (
                <>
                  <FileSpreadsheet className="w-10 h-10 text-orange-400/60" />
                  <div>
                    <span className="text-white font-medium">Click to select CSV or Excel file</span>
                    <p className="text-white/40 text-xs mt-1">
                      Games will be created automatically if they don&apos;t exist
                    </p>
                  </div>
                </>
              )}
            </label>
          </div>

          {spreadsheetStats && (
            <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-5 h-5 text-orange-400" />
                <h4 className="text-orange-400 font-medium">Spreadsheet Import Complete!</h4>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-white/60">Games Created:</div>
                <div className="text-white">{spreadsheetStats.gamesCreated}</div>
                <div className="text-white/60">Achievements Added:</div>
                <div className="text-white">{spreadsheetStats.achievementsAdded}</div>
                <div className="text-white/60">Achievements Updated:</div>
                <div className="text-white">{spreadsheetStats.achievementsUpdated}</div>
              </div>
              {spreadsheetStats.errors && spreadsheetStats.errors.length > 0 && (
                <div className="mt-3 pt-3 border-t border-orange-500/30">
                  <p className="text-red-400 text-sm font-medium mb-2">Errors ({spreadsheetStats.errors.length}):</p>
                  <div className="max-h-32 overflow-y-auto text-xs text-red-300/80 space-y-1">
                    {spreadsheetStats.errors.map((err, i) => (
                      <p key={i}>{err}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Export Card */}
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <Download className="w-5 h-5 text-green-400" />
              Export Data
            </CardTitle>
            <CardDescription className="text-white/50">
              Download all your data as a JSON backup file.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-[#121212] rounded-lg border border-white/5">
              <h4 className="text-white font-medium mb-2">What&apos;s included:</h4>
              <ul className="text-white/60 text-sm space-y-1">
                <li>• All games with box art URLs</li>
                <li>• All achievements with images, scores, and dates</li>
                <li>• Custom tags, platforms, and systems</li>
              </ul>
            </div>

            <Button
              onClick={handleExport}
              disabled={exporting}
              data-testid="export-btn"
              className="w-full bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wider"
            >
              {exporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export to JSON
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* JSON Import Card */}
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <FileJson className="w-5 h-5 text-blue-400" />
              Import JSON Backup
            </CardTitle>
            <CardDescription className="text-white/50">
              Restore from a previously exported JSON file.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Import Mode</Label>
              <Select value={importMode} onValueChange={setImportMode}>
                <SelectTrigger data-testid="import-mode-select" className="bg-black/50 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="merge">
                    <div className="flex flex-col">
                      <span>Merge</span>
                      <span className="text-white/40 text-xs">Add new items, skip duplicates</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="replace">
                    <div className="flex flex-col">
                      <span className="text-red-400">Replace All</span>
                      <span className="text-white/40 text-xs">Delete existing data first</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {importMode === "replace" && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                <p className="text-red-400 text-sm">
                  Replace mode will delete ALL existing data before importing.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Select JSON File</Label>
              <Input
                type="file"
                accept=".json,application/json"
                onChange={handleFileSelect}
                disabled={importing}
                data-testid="import-file-input"
                className="bg-black/50 border-white/10"
              />
            </div>

            {importing && (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                <span className="ml-3 text-white/60">Importing...</span>
              </div>
            )}

            {importStats && (
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <h4 className="text-green-400 font-medium">Import Complete!</h4>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-white/60">Games:</div>
                  <div className="text-white">
                    {importStats.games.added} added, {importStats.games.skipped} skipped
                  </div>
                  <div className="text-white/60">Achievements:</div>
                  <div className="text-white">
                    {importStats.achievements.added} added, {importStats.achievements.skipped} skipped
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Replace Warning Dialog */}
      <AlertDialog open={showReplaceWarning} onOpenChange={setShowReplaceWarning}>
        <AlertDialogContent className="bg-[#0A0A0A] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              Replace All Data?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete ALL your existing data before importing. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmReplace}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-replace-btn"
            >
              Yes, Replace All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AdSense */}
      <AdBanner />
    </div>
    </ProGate>
  );
}
